import React, { useEffect, useState } from "react"
import { Pressable, StyleSheet, View } from "react-native"
import AvatarM from "@common/components/avatar/AvatarM"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextButtonM from "@common/components/button/TextButtonM"
import { theme } from "@common/theme/theme"
import {
    dismissControlOpacity,
    EVENT_CATALOG,
    formatElapsed,
} from "./eventsCatalog"

const EventCard = ({ event, onPrimaryAction, onDismiss, compact, now: nowProp }) => {
    const meta = EVENT_CATALOG[event.type]
    const firstname = event.student?.firstname ?? "?"
    // Use a shared `now` from the parent when provided so the whole panel ticks
    // off a single timer; only self-tick when rendered standalone.
    const [localNow, setLocalNow] = useState(Date.now())
    const [hovered, setHovered] = useState(false)

    useEffect(() => {
        if (nowProp != null) return undefined
        const t = setInterval(() => setLocalNow(Date.now()), 1000)
        return () => clearInterval(t)
    }, [nowProp])

    const now = nowProp ?? localNow
    const elapsed = formatElapsed(event.startedAt, now)
    const isResolved = event.isResolved
    const cta = event.cta
    const isPassive = cta.kind === "passive"
    const canDismiss = !!onDismiss && !isPassive && !isResolved
    const dismissOpacity = dismissControlOpacity(hovered)

    const cardBg = isResolved ? theme.colors.successLight : theme.colors.white
    const borderColor = isResolved ? theme.colors.success : meta.severityColor

    return (
        <Pressable
            onHoverIn={() => setHovered(true)}
            onHoverOut={() => setHovered(false)}
            style={[
                styles.card,
                { backgroundColor: cardBg, borderLeftColor: borderColor },
                compact && { paddingVertical: 8 },
            ]}
        >
            <View style={styles.row}>
                <View style={styles.avatar}>
                    <AvatarM user={event.student?.raw} size={34} />
                    <View style={styles.statusDot}>
                        <TextM fontSize={10}>{meta.emoji}</TextM>
                    </View>
                </View>

                <View style={styles.body}>
                    <TextM
                        color={theme.colors.blackLight}
                        fontSize={13}
                        fontWeight="SemiBold"
                        wrap
                    >
                        <TextM fontWeight="ExtraBold" fontSize={13}>
                            {firstname}
                        </TextM>
                        {" "}
                        {isResolved
                            ? event.resolvedLabel
                            : event.verb ?? meta.verb}
                    </TextM>
                    {event.escalated && !isResolved ? (
                        <TextM
                            color={theme.colors.error}
                            fontSize={10}
                            fontWeight="ExtraBold"
                            style={{ marginTop: 2 }}
                        >
                            ⚡ Sans réponse — à toi de jouer
                        </TextM>
                    ) : null}
                </View>

                {/* The elapsed counter is useful in the drawer but noise in the
                    compact snackbar (and it collided with the close X), so it
                    is hidden in the compact variant. */}
                {!compact && (
                    <TextM
                        color={theme.colors.greyAlt}
                        fontSize={11}
                        fontWeight="SemiBold"
                        style={{
                            marginLeft: 6,
                            marginRight: canDismiss ? 22 : 0,
                        }}
                    >
                        {isResolved ? "à l'instant" : elapsed}
                    </TextM>
                )}
            </View>

            {canDismiss && (
                <Pressable
                    onPress={() => onDismiss(event)}
                    style={[styles.dismissCross, { opacity: dismissOpacity }]}
                    hitSlop={8}
                >
                    <TextM
                        color={theme.colors.greyAlt}
                        fontSize={13}
                        fontWeight="ExtraBold"
                    >
                        ✕
                    </TextM>
                </Pressable>
            )}

            {!isResolved && cta.kind !== "none" && (
                <View style={styles.actionsRow}>
                    {isPassive ? (
                        <TextButtonM
                            label={cta.label}
                            color={theme.colors.sam}
                            onPress={() => onPrimaryAction(event)}
                            fontWeight="ExtraBold"
                            style={{ fontSize: 11, marginRight: 6 }}
                        />
                    ) : (
                        <FilledButtonM
                            label={cta.label}
                            color={theme.colors.primary}
                            onPress={() => onPrimaryAction(event)}
                            style={{
                                width: null,
                                paddingHorizontal: 10,
                                marginRight: 6,
                                marginBottom: 0,
                            }}
                            contentStyle={{ height: 32 }}
                            labelStyle={{ fontSize: 11 }}
                            wrapText
                        />
                    )}
                </View>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create({
    card: {
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.lighter,
        borderLeftWidth: 3,
        position: "relative",
    },
    row: {
        flexDirection: "row",
        alignItems: "flex-start",
    },
    avatar: {
        width: 34,
        height: 34,
        marginRight: 10,
        position: "relative",
    },
    statusDot: {
        position: "absolute",
        bottom: -2,
        right: -4,
        backgroundColor: theme.colors.white,
        width: 18,
        height: 18,
        borderRadius: 100,
        alignItems: "center",
        justifyContent: "center",
    },
    body: {
        flex: 1,
        minWidth: 0,
    },
    actionsRow: {
        flexDirection: "row",
        marginTop: 8,
        marginLeft: 44,
        flexWrap: "wrap",
    },
    dismissCross: {
        position: "absolute",
        top: 6,
        right: 8,
        width: 22,
        height: 22,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 100,
    },
})

export default EventCard
